package com.evoapps.evoshift.domain.model

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.addJsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray

/*
 * SH-211: formatted lesson text. A lesson is a [RichDocument]: paragraphs ([RichBlock]) with an
 * optional list kind and nesting level, a text size, and inline [RichSpan]s (bold, italic,
 * underline, strikethrough, highlight, a colour from a fixed palette, a link). It is stored as a
 * small versioned JSON document ([RichText.encode], docs/training.md "Formatted lessons"), never as
 * HTML: the readers build Compose text from it, so nothing in it can run, and links are only
 * http, https or mailto ([RichText.safeLink]). The page's plain `body` keeps a text version
 * ([RichText.plainText]) that older app versions show instead.
 */

enum class RichListKind { NUMBERED, LETTERED, BULLET }

/** Text size steps: the readers map them to the theme's type scale. */
enum class RichSize { SMALL, NORMAL, LARGE, HEADING }

enum class RichMark { BOLD, ITALIC, UNDERLINE, STRIKETHROUGH, HIGHLIGHT }

/**
 * The text colours a lesson may use: names, not values. Each reader maps them to a shade that is
 * readable on the surface it draws on (light screens, dark cards) in every theme.
 */
enum class RichColor { RED, ORANGE, GREEN, BLUE, PURPLE, GREY }

sealed interface RichStyle {
    data class Mark(val mark: RichMark) : RichStyle
    data class Colour(val color: RichColor) : RichStyle
    /** A safe address ([RichText.safeLink]). */
    data class Link(val url: String) : RichStyle
}

/** [style] on the characters [start] until [end] (exclusive) of its block. */
data class RichSpan(val start: Int, val end: Int, val style: RichStyle)

/** One paragraph or list item. [level] (0..[RichText.MAX_LEVEL]) nests list items. */
data class RichBlock(
    val text: String,
    val spans: List<RichSpan> = emptyList(),
    val list: RichListKind? = null,
    val level: Int = 0,
    val size: RichSize = RichSize.NORMAL
)

data class RichDocument(val blocks: List<RichBlock>) {
    /** The characters of the lesson, paragraphs joined by new lines (no list markers). */
    val text: String get() = blocks.joinToString("\n") { it.text }
    val isBlank: Boolean get() = blocks.all { it.text.isBlank() }

    companion object {
        val EMPTY = RichDocument(listOf(RichBlock("")))

        /** A plain-text lesson of before (SH-126): one paragraph per line, nothing formatted. */
        fun plain(text: String) = RichDocument(text.split('\n').map { RichBlock(it) })
    }
}

object RichText {
    /** The storage format's version; a newer document is shown from its plain text. */
    const val VERSION = 1
    const val MAX_LEVEL = 2
    /** Characters of text in a lesson (as [CourseRules.MAX_BODY] before). */
    const val MAX_TEXT = CourseRules.MAX_BODY
    const val MAX_BLOCKS = 400
    const val MAX_SPANS = 2000
    /** The stored JSON (firestore.rules validCourse checks the same). */
    const val MAX_ENCODED = 24_000
    /** The plain-text version, with list markers and link addresses (firestore.rules too). */
    const val MAX_PLAIN = 6_000
    const val MAX_LINK = 500

    private val json = Json { ignoreUnknownKeys = true }

    /**
     * [raw] as a link a lesson may hold: http or https with a host, or mailto with an address;
     * trimmed, at most [MAX_LINK] characters, without spaces or control characters. A bare
     * "example.com/x" becomes https. Null for anything else (javascript:, data:, file:, ...).
     */
    fun safeLink(raw: String?): String? {
        val value = raw?.trim() ?: return null
        if (value.isEmpty() || value.length > MAX_LINK) return null
        if (value.any { it.isWhitespace() || it.isISOControl() || it in "<>\"`\\{}|^" }) return null
        val head = value.substringBefore('/')
        if (':' !in head || value.startsWith("localhost:")) {
            // No scheme: an address typed without one.
            return if ('.' in head) safeLink("https://$value") else null
        }
        val scheme = value.substringBefore(':').lowercase()
        val rest = value.substring(scheme.length + 1)
        return when (scheme) {
            "mailto" -> {
                val address = rest.substringBefore('?')
                val at = address.indexOf('@')
                value.takeIf {
                    at > 0 && at < address.length - 1 && address.count { it == '@' } == 1 &&
                        '.' in address.substring(at) && address.none { it in ":/" }
                }
            }
            "http", "https" -> {
                if (!rest.startsWith("//")) return null
                val authority = rest.substring(2).substringBefore('/').substringBefore('?').substringBefore('#')
                val host = authority.substringBefore(':')
                val port = authority.substringAfter(':', "")
                val hostOk = host.isNotEmpty() && '.' in host && !host.startsWith('.') && !host.endsWith('.') &&
                    host.all { it.isLetterOrDigit() || it == '.' || it == '-' }
                value.takeIf { hostOk && port.all(Char::isDigit) && port.length <= 5 }
            }
            else -> null
        }
    }

    /**
     * [document] within the limits: no line breaks or control characters inside a paragraph,
     * levels 0..[MAX_LEVEL] (0 outside lists), spans inside their text, unsafe links dropped, and
     * per paragraph each style painted once ([normalizeSpans]). At least one paragraph.
     */
    fun sanitize(document: RichDocument): RichDocument {
        var spansLeft = MAX_SPANS
        val blocks = document.blocks.take(MAX_BLOCKS).map { block ->
            val text = block.text.map { c -> if (c == '\n' || c == '\r' || (c.isISOControl() && c != '\t')) ' ' else c }.joinToString("")
            val spans = normalizeSpans(text.length, block.spans.mapNotNull { span ->
                when (val style = span.style) {
                    is RichStyle.Link -> safeLink(style.url)?.let { span.copy(style = RichStyle.Link(it)) }
                    else -> span
                }
            }).take(spansLeft.coerceAtLeast(0))
            spansLeft -= spans.size
            block.copy(
                text = text,
                spans = spans,
                level = if (block.list == null) 0 else block.level.coerceIn(0, MAX_LEVEL)
            )
        }
        return RichDocument(blocks.ifEmpty { listOf(RichBlock("")) })
    }

    /** Which kind of style [this] is: a mark of its own, any colour, any link. */
    internal val RichStyle.slot: String
        get() = when (this) {
            is RichStyle.Mark -> mark.name
            is RichStyle.Colour -> "COLOUR"
            is RichStyle.Link -> "LINK"
        }

    private val slotOrder = RichMark.entries.map { it.name } + listOf("COLOUR", "LINK")

    /**
     * [spans] over a text of [length] characters as the fewest spans that look the same: clipped
     * to the text, later spans painting over earlier ones of the same slot (a colour replaces a
     * colour), touching equal ones merged; sorted by start, then by slot.
     */
    fun normalizeSpans(length: Int, spans: List<RichSpan>): List<RichSpan> {
        if (length <= 0 || spans.isEmpty()) return emptyList()
        val painted = HashMap<String, Array<RichStyle?>>()
        spans.forEach { span ->
            val start = span.start.coerceIn(0, length)
            val end = span.end.coerceIn(0, length)
            if (start >= end) return@forEach
            val cells = painted.getOrPut(span.style.slot) { arrayOfNulls(length) }
            for (i in start until end) cells[i] = span.style
        }
        val out = mutableListOf<RichSpan>()
        slotOrder.forEach { slot ->
            val cells = painted[slot] ?: return@forEach
            var i = 0
            while (i < length) {
                val style = cells[i]
                if (style == null) { i++; continue }
                var j = i + 1
                while (j < length && cells[j] == style) j++
                out += RichSpan(i, j, style)
                i = j
            }
        }
        return out.sortedWith(compareBy<RichSpan> { it.start }.thenBy { slotOrder.indexOf(it.style.slot) })
    }

    /** "a".."z", "aa".. for [number] 1, 2, ... */
    fun letters(number: Int): String {
        var n = number.coerceAtLeast(1)
        val out = StringBuilder()
        while (n > 0) {
            n -= 1
            out.insert(0, 'a' + n % 26)
            n /= 26
        }
        return out.toString()
    }

    private val bullets = listOf("•", "–", "•")

    /**
     * Each paragraph's list marker ("1.", "a)", "•"), null outside lists. Numbers count the items
     * of one kind on one level and start again after a paragraph that is no list item, after a
     * different kind on that level, and on a deeper level after each item above it.
     */
    fun markers(document: RichDocument): List<String?> {
        val counters = IntArray(MAX_LEVEL + 1)
        val kinds = arrayOfNulls<RichListKind>(MAX_LEVEL + 1)
        return document.blocks.map { block ->
            val kind = block.list
            if (kind == null) {
                counters.fill(0); kinds.fill(null)
                return@map null
            }
            val level = block.level.coerceIn(0, MAX_LEVEL)
            for (deeper in level + 1..MAX_LEVEL) { counters[deeper] = 0; kinds[deeper] = null }
            if (kinds[level] != kind) { counters[level] = 0; kinds[level] = kind }
            counters[level] += 1
            when (kind) {
                RichListKind.NUMBERED -> "${counters[level]}."
                RichListKind.LETTERED -> "${letters(counters[level])})"
                RichListKind.BULLET -> bullets[level]
            }
        }
    }

    /** The indent of a list item of [level] in the plain text. */
    fun plainIndent(level: Int): String = "   ".repeat(level.coerceIn(0, MAX_LEVEL))

    /**
     * The text older app versions show (the page's `body`): one line per paragraph, list items
     * with their indent and marker, a link's address after its text when they differ; at most
     * [MAX_PLAIN] characters. A plain lesson of before comes back unchanged.
     */
    fun plainText(document: RichDocument): String {
        val markers = markers(document)
        return document.blocks.mapIndexed { index, block ->
            val text = buildString {
                var at = 0
                block.spans.filter { it.style is RichStyle.Link }.sortedBy { it.start }.forEach { span ->
                    val url = (span.style as RichStyle.Link).url
                    val end = span.end.coerceAtMost(block.text.length)
                    if (span.start < at || end <= span.start) return@forEach
                    append(block.text, at, end)
                    val label = block.text.substring(span.start, end)
                    val shown = url.removePrefix("mailto:")
                    if (label.trim() != url && label.trim() != shown) append(" (").append(shown).append(')')
                    at = end
                }
                append(block.text, at.coerceAtMost(block.text.length), block.text.length)
            }
            markers[index]?.let { plainIndent(block.level) + it + " " + text } ?: text
        }.joinToString("\n").take(MAX_PLAIN)
    }

    // --- Storage -------------------------------------------------------------------------------

    /**
     * The stored form: `{"v":1,"blocks":[{"text":"…","list":"NUMBERED","level":1,"size":"HEADING",
     * "spans":[{"s":0,"e":4,"t":"BOLD"},{"s":5,"e":9,"t":"COLOUR","c":"RED"},{"s":0,"e":4,"t":"LINK","u":"https://…"}]}]}`.
     * Defaults (no list, level 0, NORMAL, no spans) are left out.
     */
    fun encode(document: RichDocument): String {
        val clean = sanitize(document)
        val root = buildJsonObject {
            put("v", VERSION)
            putJsonArray("blocks") {
                clean.blocks.forEach { block ->
                    addJsonObject {
                        put("text", block.text)
                        block.list?.let { put("list", it.name) }
                        if (block.level > 0) put("level", block.level)
                        if (block.size != RichSize.NORMAL) put("size", block.size.name)
                        if (block.spans.isNotEmpty()) putJsonArray("spans") {
                            block.spans.forEach { span ->
                                addJsonObject {
                                    put("s", span.start)
                                    put("e", span.end)
                                    when (val style = span.style) {
                                        is RichStyle.Mark -> put("t", style.mark.name)
                                        is RichStyle.Colour -> { put("t", "COLOUR"); put("c", style.color.name) }
                                        is RichStyle.Link -> { put("t", "LINK"); put("u", style.url) }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        return root.toString()
    }

    /**
     * The document stored as [stored], sanitised; null when there is none, it is unreadable, or
     * it is of a newer [VERSION] (then the plain text is shown). Unknown list kinds, sizes and
     * styles of this version are ignored, so a later minor addition degrades to plain text.
     */
    fun decode(stored: String?): RichDocument? {
        if (stored.isNullOrBlank() || stored.length > MAX_ENCODED * 2) return null
        val root = runCatching { json.parseToJsonElement(stored) as? JsonObject }.getOrNull() ?: return null
        val version = (root["v"] as? JsonPrimitive)?.intOrNull ?: return null
        if (version < 1 || version > VERSION) return null
        val blocks = (root["blocks"] as? JsonArray) ?: return null
        fun JsonObject.string(key: String) = (this[key] as? JsonPrimitive)?.takeIf { it.isString }?.contentOrNull
        fun JsonObject.int(key: String) = (this[key] as? JsonPrimitive)?.takeIf { !it.isString }?.intOrNull
        val parsed = blocks.mapNotNull { element ->
            val block = element as? JsonObject ?: return@mapNotNull null
            val text = block.string("text") ?: return@mapNotNull null
            val spans = (block["spans"] as? JsonArray).orEmpty().mapNotNull { e ->
                val span = e as? JsonObject ?: return@mapNotNull null
                val start = span.int("s") ?: return@mapNotNull null
                val end = span.int("e") ?: return@mapNotNull null
                val style = when (val type = span.string("t")) {
                    "COLOUR" -> RichColor.entries.firstOrNull { it.name == span.string("c") }?.let { RichStyle.Colour(it) }
                    "LINK" -> span.string("u")?.let { RichStyle.Link(it) }
                    else -> RichMark.entries.firstOrNull { it.name == type }?.let { RichStyle.Mark(it) }
                } ?: return@mapNotNull null
                RichSpan(start, end, style)
            }
            RichBlock(
                text = text,
                spans = spans,
                list = RichListKind.entries.firstOrNull { it.name == block.string("list") },
                level = block.int("level") ?: 0,
                size = RichSize.entries.firstOrNull { it.name == block.string("size") } ?: RichSize.NORMAL
            )
        }
        if (parsed.isEmpty()) return null
        return sanitize(RichDocument(parsed))
    }

    /** Whether [document] has anything beyond plain paragraphs (else storing it adds nothing). */
    fun isFormatted(document: RichDocument): Boolean =
        document.blocks.any { it.spans.isNotEmpty() || it.list != null || it.size != RichSize.NORMAL }

    /** Why [document] cannot be stored, or null: too much text, too many paragraphs or styles. */
    fun tooLarge(document: RichDocument): Boolean =
        document.text.length > MAX_TEXT || document.blocks.size > MAX_BLOCKS ||
            document.blocks.sumOf { it.spans.size } > MAX_SPANS || encode(document).length > MAX_ENCODED

}
